import type { AssetTypeIdentifier, AssetTypeStatus, ProcedureStep, Stored, User } from "@overmap-ai/common"
import { ConditionManager, FieldSchema } from "@overmap-ai/forms"

import type { ProcedureInitiativeData } from "../../typings"
import { getProcedureStepCompletionStatus } from "../../utils/procedures"
import { AssetTypeStatusFilterDef } from "../defs/AssetTypeStatusFilterDef"
import { CreatedByFilterDef } from "../defs/CreatedByFilterDef"
import { ProcedureStepAssigneeFilterDef } from "../defs/ProcedureStepAssigneeFilterDef"
import { ProcedureStepCompletionStatusFilterDef } from "../defs/ProcedureStepCompletionStatusFilterDef"
import { ProcedureStepReviewerFilterDef } from "../defs/ProcedureStepReviewerFilterDef"
import { ProcedureStepReviewStatusFilterDef } from "../defs/ProcedureStepReviewStatusFilterDef"
import { SubmittedAtFilterDef } from "../defs/SubmittedAtFilterDef"
import { getFilterDefConfigsForFieldSchema, getFilterDefsOfIdentifiers } from "../defs/utils"
import { FilterSchema } from "./FilterSchema"
import type { AnyFilterSchemaDefConfig } from "./typings"
import { createFilterDefConfig } from "./utils"

interface ProcedureInitiativeDataFilterSchemaConfig {
	procedureTypeFieldSchema: FieldSchema
	procedureSteps: Stored<ProcedureStep>[]
	procedureStepFieldSchemasAndConditions: Record<ProcedureStep["uuid"], [FieldSchema, ConditionManager]>
	users: User[]
	assetTypeIdentifiers: Stored<AssetTypeIdentifier>[]
	assetTypeFieldSchema: FieldSchema
	assetTypeStatuses: Stored<AssetTypeStatus>[]
}

export const getProcedureInitiativeDataFilterSchema = (config: ProcedureInitiativeDataFilterSchemaConfig) => {
	const {
		procedureTypeFieldSchema,
		procedureSteps,
		procedureStepFieldSchemasAndConditions,
		users,
		assetTypeIdentifiers,
		assetTypeFieldSchema,
		assetTypeStatuses,
	} = config

	const filterDefs: AnyFilterSchemaDefConfig<ProcedureInitiativeData>[] = [
		createFilterDefConfig({
			filterDef: new CreatedByFilterDef(users),
			accessorFn: ({ procedure }) => procedure.created_by ?? -1,
		}),
		createFilterDefConfig({
			filterDef: new SubmittedAtFilterDef(),
			accessorFn: ({ procedure }) => new Date(procedure.submitted_at),
		}),
	]

	if (assetTypeStatuses.length > 0) {
		filterDefs.push(
			createFilterDefConfig({
				filterDef: new AssetTypeStatusFilterDef(assetTypeStatuses),
				accessorFn: ({ asset }) => asset.status,
			}),
		)
	}

	if (assetTypeIdentifiers.length > 0) {
		filterDefs.push(
			...getFilterDefsOfIdentifiers<Stored<AssetTypeIdentifier>, ProcedureInitiativeData>(
				assetTypeIdentifiers,
				(identifier) => (data) => data.assetTypeIdentifierValues[identifier.uuid]?.value ?? null,
			),
		)
	}

	if (assetTypeFieldSchema.getFields().length > 0) {
		filterDefs.push(
			...getFilterDefConfigsForFieldSchema<ProcedureInitiativeData>(assetTypeFieldSchema, (data) => [
				assetTypeFieldSchema.deserializeValues(data.assetTypeFieldValues?.values ?? {}),
				data.assetTypeFieldValuesAttachments,
			]),
		)
	}

	filterDefs.push(
		...getFilterDefConfigsForFieldSchema<ProcedureInitiativeData>(procedureTypeFieldSchema, (data) => [
			procedureTypeFieldSchema.deserializeValues(data.procedureFieldValues?.values ?? {}),
			data.procedureFieldValuesAttachments,
		]),
	)

	for (const procedureStep of procedureSteps) {
		const [fieldSchema, conditionManager] = procedureStepFieldSchemasAndConditions[procedureStep.uuid]

		filterDefs.push(
			createFilterDefConfig({
				filterDef: new ProcedureStepCompletionStatusFilterDef(procedureStep),
				accessorFn: (data) =>
					getProcedureStepCompletionStatus(
						fieldSchema,
						conditionManager,
						data.procedureStepFieldValues[procedureStep.uuid]?.values,
						!data.procedureStepFieldValues[procedureStep.uuid]?.published_at,
					),
			}),
			createFilterDefConfig<ProcedureInitiativeData, ProcedureStepReviewStatusFilterDef>({
				filterDef: new ProcedureStepReviewStatusFilterDef(procedureStep),
				accessorFn: (data) => data.procedureStepFieldValuesReviews[procedureStep.uuid]?.type ?? null,
			}),
			createFilterDefConfig({
				filterDef: new ProcedureStepAssigneeFilterDef(procedureStep, users),
				accessorFn: (data) =>
					(data.procedureStepAssignees[procedureStep.uuid] ?? []).map(
						(procedureStepAssignee) => procedureStepAssignee.user,
					),
			}),
			createFilterDefConfig({
				filterDef: new ProcedureStepReviewerFilterDef(procedureStep, users),
				accessorFn: (data) =>
					(data.procedureStepReviewers[procedureStep.uuid] ?? []).map(
						(procedureStepReviewer) => procedureStepReviewer.user,
					),
			}),
			...getFilterDefConfigsForFieldSchema<ProcedureInitiativeData>(fieldSchema, (data) => [
				fieldSchema.deserializeValues(data.procedureStepFieldValues[procedureStep.uuid]?.values ?? {}),
				data.procedureStepFieldValuesAttachments[procedureStep.uuid] ?? [],
			]),
		)
	}

	return FilterSchema.create<ProcedureInitiativeData>({
		filterDefs,
	})
}
